import { useRef, useState, type ComponentType, type RefAttributes } from 'react';
import { Form, Input, Button, Space, message, Alert, Radio, Tag, Typography, Checkbox } from 'antd';
import { ArrowLeftOutlined, LockOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import ReCAPTCHA, { type ReCAPTCHAProps } from 'react-google-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { setCredentials } from '@/redux/slices/authSlice';
import { authService, type LoginCredentials } from '@/services/auth.service';
import type { IdentifyTenant } from '@/mocks/auth';
import type { AppDispatch, RootState } from '@/redux';
import { ROUTES } from '@/constants/routes';
import { AuthLayout } from '@/components/Layout/AuthLayout';
import { SYSTEM_ROLE_IDS } from '@/mocks/roles';
import { activateProactiveAccessTokenRefresh } from '@/services/api';
import { autoInitPosShiftStore } from '@/utils/posShift';

const { Text } = Typography;

type ReCAPTCHAInstance = InstanceType<typeof ReCAPTCHA>;
const ReCAPTCHAComponent = ReCAPTCHA as unknown as ComponentType<
  ReCAPTCHAProps & RefAttributes<ReCAPTCHAInstance>
>;

type Step = 'login' | 'tenant';

const PLAN_COLORS: Record<NonNullable<IdentifyTenant['planTier']>, string> = {
  lite: 'default',
  starter: 'blue',
  professional: 'gold',
  enterprise: 'red',
};

const PLAN_LABELS: Record<NonNullable<IdentifyTenant['planTier']>, string> = {
  lite: 'Lite',
  starter: 'Starter',
  professional: 'Pro',
  enterprise: 'Enterprise',
};

/**
 * Đăng nhập xong thì đi đâu (TCR-1521).
 *
 * Người giao hàng về THẲNG app rút gọn `/shipper`. Trước đây mọi vai trò đều rơi về
 * `/dashboard`: shipper nhìn thấy bảng điều khiển ERP mà họ gần như không có quyền gì
 * ở đó, và KHÔNG có đường nào tới app của mình ngoài việc tự gõ URL — thanh bên ERP
 * cũng không có mục nào trỏ tới `/shipper`.
 *
 * Vai trò khác giữ nguyên hành vi cũ.
 */
function landingRouteFor(role?: string): string {
  return role === 'shipper' ? ROUTES.shipper.orders : ROUTES.dashboard;
}

export function LoginPage() {
  const [step, setStep] = useState<Step>('login');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [tenants, setTenants] = useState<IdentifyTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<IdentifyTenant | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef<ReCAPTCHAInstance>(null);
  const [loginForm] = Form.useForm<{ identifier: string; password: string; remember_me?: boolean }>();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const currentRole = useSelector((state: RootState) => state.auth.user?.role);
  const navigate = useNavigate();

  const identifyMutation = useMutation({
    mutationFn: (input: string) => authService.identify(input),
    onError: (err: unknown) => {
      const errData = (err as { response?: { data?: { error?: { message?: string; code?: string }; message?: string } } })?.response?.data;
      const beCode = errData?.error?.code;
      if (beCode === 'USER_NOT_FOUND') {
        setErrorMsg('SĐT/email chưa được đăng ký. Vui lòng kiểm tra lại hoặc đăng ký tài khoản mới.');
        return;
      }
      const beMsg = errData?.error?.message || errData?.message || (err as Error)?.message;
      setErrorMsg(typeof beMsg === 'string' ? beMsg : 'Không kiểm tra được thông tin');
    },
  });

  const loginMutation = useMutation({
    mutationFn: (payload: LoginCredentials) => authService.login(payload),
    onSuccess: (data) => {
      const loginUser = {
        ...data.user,
        roleIds: data.user.roleIds.length
          ? data.user.roleIds
          : [SYSTEM_ROLE_IDS.owner],
      };
      dispatch(
        setCredentials({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
          user: loginUser,
        }),
      );
      activateProactiveAccessTokenRefresh(data.expires_in);
      autoInitPosShiftStore(loginUser);
      message.success('Đăng nhập thành công');

      if (data.must_set_password) {
        navigate(ROUTES.auth.setPassword, { replace: true });
      } else if (data.must_change_password) {
        navigate(ROUTES.auth.changePassword, { replace: true });
      } else if (data.needs_store_selection) {
        navigate(ROUTES.auth.selectStore, { replace: true });
      } else {
        navigate(landingRouteFor(loginUser.role), { replace: true });
      }
    },
    onError: (err: unknown) => {
      const errData = (err as { response?: { data?: { error?: { message?: string; code?: string }; message?: string } } })?.response?.data;
      const beCode = errData?.error?.code;
      if (beCode === 'INVALID_CREDENTIALS') {
        setErrorMsg('Mật khẩu không chính xác');
        return;
      }
      if (beCode === 'CAPTCHA_REQUIRED') {
        setCaptchaRequired(true);
        setRecaptchaToken('');
        setErrorMsg('Vui lòng xác nhận bạn không phải robot');
        return;
      }
      const beMsg = errData?.error?.message || errData?.message || (err as Error)?.message;
      setErrorMsg(typeof beMsg === 'string' ? beMsg : 'Đăng nhập thất bại');
    },
  });

  const handleCombinedSubmit = async (values: {
    identifier: string;
    password: string;
    remember_me?: boolean;
  }) => {
    setErrorMsg(null);
    const trimmedId = values.identifier.trim().toLowerCase();
    setIdentifier(trimmedId);
    setPassword(values.password);
    setRememberMe(!!values.remember_me);

    try {
      const identifyData = await identifyMutation.mutateAsync(trimmedId);
      if (!identifyData.userExists || !identifyData.tenants.length) {
        setErrorMsg('SĐT/email không tồn tại hoặc chưa thuộc chi nhánh nào.');
        return;
      }
      if (!identifyData.hasPassword) {
        try {
          const recover = await authService.recoverInit({
            identifier: trimmedId,
            preferredChannel: 'zns',
          });
          message.info('Tài khoản chưa đặt mật khẩu — gửi OTP khôi phục');
          navigate(ROUTES.auth.otp, {
            state: {
              context: 'recover',
              sessionId: recover.session_id,
              channel: recover.channel,
              availableChannels: recover.available_channels,
              phoneMasked: recover.phone_masked,
              emailMasked: recover.email_masked,
            },
          });
          return;
        } catch (err: unknown) {
          const beMsg =
            (err as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message;
          setErrorMsg(beMsg || 'Không gửi được OTP khôi phục');
          return;
        }
      }

      setTenants(identifyData.tenants);

      if (identifyData.tenants.length > 1) {
        setStep('tenant');
        return;
      }

      const tenantId = identifyData.tenants[0]?.tenantId;
      setSelectedTenant(identifyData.tenants[0] || null);

      loginMutation.mutate({
        identifier: trimmedId,
        password: values.password,
        ...(tenantId ? { tenant_id: tenantId } : {}),
        remember_me: !!values.remember_me,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      } satisfies LoginCredentials);
    } catch (err: unknown) {
      // Fallback: execute direct login if identify fails
      const errData = (err as { response?: { data?: { error?: { message?: string; code?: string }; message?: string } } })?.response?.data;
      if (errData?.error?.code === 'USER_NOT_FOUND') {
        setErrorMsg('SĐT/email chưa được đăng ký. Vui lòng kiểm tra lại hoặc đăng ký tài khoản mới.');
        return;
      }
      loginMutation.mutate({
        identifier: trimmedId,
        password: values.password,
        remember_me: !!values.remember_me,
        ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
      } satisfies LoginCredentials);
    }
  };

  const handleSelectTenant = () => {
    if (!selectedTenant) {
      setErrorMsg('Vui lòng chọn chi nhánh');
      return;
    }
    setErrorMsg(null);
    loginMutation.mutate({
      identifier,
      password,
      tenant_id: selectedTenant.tenantId,
      remember_me: rememberMe,
      ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
    } satisfies LoginCredentials);
  };

  const handleRecaptchaChange = (token: string | null) => {
    if (!token) return;
    setRecaptchaToken(token);
    if (password && identifier) {
      loginMutation.mutate({
        identifier,
        password,
        ...(selectedTenant ? { tenant_id: selectedTenant.tenantId } : {}),
        remember_me: rememberMe,
        recaptcha_token: token,
      } satisfies LoginCredentials);
    }
  };

  const handleBack = () => {
    setErrorMsg(null);
    setCaptchaRequired(false);
    setRecaptchaToken('');
    recaptchaRef.current?.reset();
    setStep('login');
    setSelectedTenant(null);
  };

  const subtitle =
    step === 'login'
      ? 'Đăng nhập để quản lý cửa hàng'
      : `Tài khoản ${identifier} thuộc nhiều chi nhánh — chọn chi nhánh để đăng nhập`;

  if (isAuthenticated) {
    // Cùng luật với nhánh sau khi đăng nhập — người giao hàng mở lại trang đăng nhập
    // (bookmark, nút back) cũng phải về app của họ, không phải bảng điều khiển ERP.
    return <Navigate to={landingRouteFor(currentRole)} replace />;
  }

  return (
    <AuthLayout
      title="TCR-ERP"
      subtitle={subtitle}
      footer={
        step === 'login' ? (
          <Link to={ROUTES.auth.forgotPassword} className="auth-footer-link">
            Quên mật khẩu
          </Link>
        ) : (
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="auth-footer-link"
            style={{ padding: 0 }}
          >
            Quay lại
          </Button>
        )
      }
    >
      {step === 'login' && (
        <Form
          form={loginForm}
          onFinish={handleCombinedSubmit}
          layout="vertical"
          initialValues={{ remember_me: true }}
          data-testid="login-identifier-form"
        >
          <Form.Item
            label={<span className="auth-form-label">E-mail</span>}
            name="identifier"
            rules={[
              { required: true, message: 'Vui lòng nhập E-mail hoặc SĐT' },
              {
                validator: (_rule, value) => {
                  if (!value || typeof value !== 'string') return Promise.resolve();
                  const trimmed = value.trim().toLowerCase();
                  const phoneRegex = /^(0|\+?84)\d{9,10}$/;
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (phoneRegex.test(trimmed) || emailRegex.test(trimmed)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Số điện thoại hoặc email không đúng định dạng'),
                  );
                },
              },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#6b6b6b' }} />}
              placeholder="Nhập địa chỉ mail"
              autoComplete="username"
              autoFocus
              className="auth-input"
              data-testid="login-identifier-input"
            />
          </Form.Item>

          <Form.Item
            label={<span className="auth-form-label">Mật khẩu</span>}
            name="password"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#6b6b6b' }} />}
              placeholder="Nhập mật khẩu"
              autoComplete="current-password"
              className="auth-input"
              data-testid="login-password-input"
            />
          </Form.Item>

          <Form.Item name="remember_me" valuePropName="checked" style={{ marginBottom: 16 }}>
            <Checkbox className="auth-checkbox">Lưu thông tin đăng nhập</Checkbox>
          </Form.Item>

          {captchaRequired && (
            <div style={{ marginBottom: 12 }}>
              <ReCAPTCHAComponent
                ref={recaptchaRef}
                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                onChange={handleRecaptchaChange}
              />
            </div>
          )}

          <div className="auth-btn-wrapper">
            <Button
              type="primary"
              loading={loginMutation.isPending || identifyMutation.isPending}
              htmlType="submit"
              className="auth-primary-btn active"
              data-testid="login-submit-btn"
            >
              Đăng nhập
            </Button>
          </div>

          {errorMsg && (
            <Alert
              type="error"
              message={errorMsg}
              showIcon
              style={{ marginTop: 16 }}
              data-testid="login-error"
            />
          )}
        </Form>
      )}

      {step === 'tenant' && (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Radio.Group
            value={selectedTenant?.tenantId}
            onChange={(e) =>
              setSelectedTenant(tenants.find((t) => t.tenantId === e.target.value) ?? null)
            }
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {tenants.map((t) => (
                <Radio
                  key={t.tenantId}
                  value={t.tenantId}
                  style={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: selectedTenant?.tenantId === t.tenantId ? '#a4193d' : '#e5e7eb',
                    borderRadius: 8,
                    padding: 12,
                    background:
                      selectedTenant?.tenantId === t.tenantId ? '#fff6f8' : '#fff',
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <ShopOutlined style={{ color: '#a4193d' }} />
                      <div>
                        <Text strong>{t.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {t.tenantCode} · vai trò {t.role}
                          {t.storeCount ? ` · ${t.storeCount} CN` : ''}
                        </Text>
                      </div>
                    </Space>
                    {t.planTier && (
                      <Tag color={PLAN_COLORS[t.planTier]} style={{ marginLeft: 8 }}>
                        {PLAN_LABELS[t.planTier]}
                      </Tag>
                    )}
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          <div className="auth-btn-wrapper">
            <Button
              type="primary"
              disabled={!selectedTenant}
              loading={loginMutation.isPending}
              onClick={handleSelectTenant}
              className={`auth-primary-btn ${selectedTenant ? 'active' : ''}`}
              data-testid="login-tenant-confirm"
            >
              Đăng nhập
            </Button>
          </div>
          {errorMsg && (
            <Alert
              type="error"
              message={errorMsg}
              showIcon
              style={{ marginTop: 16 }}
              data-testid="login-error"
            />
          )}
        </Space>
      )}
    </AuthLayout>
  );
}
